import React, { type ReactNode, Suspense, lazy, useEffect, useLayoutEffect, useRef } from 'react';
import { usePanelRef } from 'react-resizable-panels';
import { useLocation } from 'react-router-dom';

import { AppSidebar } from '@/components/app-sidebar';
import { useRememberOpenedFrom } from '@/components/campaigns/plan/entry/opened-from';
import { RepurposeWorkbenchProvider } from '@/components/create/v2/contexts/RepurposeWorkbenchContext';
import Navbar from '@/components/layout/Navbar';
import { NetworkStatusDialog } from '@/components/layout/NetworkStatusDialog';
import PageHeaderManager from '@/components/layout/PageHeaderManager';
import { SettingsDialogProvider } from '@/components/settings/SettingsDialogProvider';
import { UnifiedErrorBoundary } from '@/components/ui/error-boundary';
import { useInspector } from '@/components/ui/inspector/hooks/useInspector';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable';
import { SidebarInset, SidebarProvider, useSidebar } from '@/components/ui/sidebar';
import { WorkspaceLinkFallback } from '@/components/workspace/WorkspaceLinkFallback';
import { useGlobalJobMonitor } from '@/hooks/useGlobalJobMonitor';
import { usePrefetchRoute } from '@/hooks/usePrefetchRoute';
import { useCanSplitRightSlot, useCanSplitRightSlotMedium } from '@/hooks/useRightSlotBreakpoints';
import { type VisibleRightPanel, useVisibleRightPanel } from '@/hooks/useVisibleRightPanel';
import { useWebMCP } from '@/hooks/useWebMCP';
import { useWorkspaceBroadcastSync } from '@/hooks/useWorkspaceBroadcastSync';
import { useWorkspaceUrlSync } from '@/hooks/useWorkspaceUrlSync';
import { cn } from '@/lib/utils';
import { closeOtherRightSlotSurfaces } from '@/stores/_right-slot-mutex';
import { useArtifactStore } from '@/stores/useArtifactStore';
import { useDocumentDialogStore } from '@/stores/useDocumentDialogStore';
import { useImageDialogStore } from '@/stores/useImageDialogStore';
import { closeCreateScopedRightSlotSurfaces, useRightDockStore } from '@/stores/useRightDockStore';
import { useVideoDialogStore } from '@/stores/useVideoDialogStore';
import { useWorkflowLibraryStore } from '@/stores/useWorkflowLibraryStore';

// F-082 (UAT 2026-06-12): these global overlays were imported statically,
// pulling their entire subtrees into the ENTRY module graph — the inspector
// alone is a ~1MB chunk (every drawer body + canvas), and SettingsDialog
// drags the full settings tree. The built HTML preloaded 262 chunks (~4.4MB)
// that all had to resolve before main.tsx could run: a 30–60s white screen
// on cold loads. React.lazy moves them out of the entry graph; they start
// fetching on first render — i.e. immediately AFTER first paint — so the
// shell appears fast and the overlays hydrate in the background. All of them
// render null until opened, so `fallback={null}` is exact.
const ArtifactDrawer = lazy(() =>
  import('@/components/create/v2/ArtifactDrawer').then((m) => ({ default: m.ArtifactDrawer })),
);
const VideoStudioDialogMount = lazy(() =>
  import('@/components/create/v2/sections/video-studio/VideoStudioDialogMount').then((m) => ({
    default: m.VideoStudioDialogMount,
  })),
);
const GlobalRightDock = lazy(() =>
  import('@/components/layout/GlobalRightDock').then((m) => ({ default: m.GlobalRightDock })),
);
const LinkedInDestinationPrompt = lazy(() =>
  import('@/components/posts/LinkedInDestinationPrompt').then((m) => ({
    default: m.LinkedInDestinationPrompt,
  })),
);
const HandoffFailureRecovery = lazy(() =>
  import('@/components/media-studio/HandoffFailureRecovery').then((m) => ({
    default: m.HandoffFailureRecovery,
  })),
);
const WorkflowLibraryDialogMount = lazy(() =>
  import('@/components/plans/WorkflowLibraryDialogMount').then((m) => ({
    default: m.WorkflowLibraryDialogMount,
  })),
);
const ProjectHubMount = lazy(() =>
  import('@/components/projects/ProjectHubMount').then((m) => ({ default: m.ProjectHubMount })),
);
const ProjectHubHydrator = lazy(() =>
  import('@/components/projects/ProjectHubMount').then((m) => ({
    default: m.ProjectHubHydrator,
  })),
);
const GlobalSearchDialog = lazy(() =>
  import('@/components/search/GlobalSearchDialog').then((m) => ({
    default: m.GlobalSearchDialog,
  })),
);
const SettingsDialog = lazy(() =>
  import('@/components/settings/SettingsDialog').then((m) => ({ default: m.SettingsDialog })),
);
const GlobalInspectorMount = lazy(() =>
  import('@/components/ui/inspector/GlobalInspectorMount').then((m) => ({
    default: m.GlobalInspectorMount,
  })),
);

function SidebarAutoCollapse() {
  const location = useLocation();
  const { state, setOpen, setOpenMobile, isMobile } = useSidebar();
  const inspectorOpen = useInspector((s) => s.open);
  const documentViewerOpen = useDocumentDialogStore((s) => s.isOpen);
  const imageViewerOpen = useImageDialogStore((s) => s.isOpen);
  const videoViewerOpen = useVideoDialogStore((s) => s.isOpen);
  const prevPathRef = useRef('');
  const viewerOpen = inspectorOpen || documentViewerOpen || imageViewerOpen || videoViewerOpen;

  // Mobile only: the sidebar is a full-screen overlay there, so leaving it
  // open after a nav tap would strand the user behind their own menu. Desktop
  // deliberately does nothing here — the rail is a persistent primitive
  // (Notion/Linear/ChatGPT all leave it exactly as the user set it across
  // navigation); collapsing it on every click read as the sidebar randomly
  // closing itself (founder report, 2026-08-11) because that's what it was.
  useEffect(() => {
    if (prevPathRef.current && prevPathRef.current !== location.pathname && isMobile) {
      setOpenMobile(false);
    }

    prevPathRef.current = location.pathname;
  }, [location.pathname, setOpenMobile, isMobile]);

  useEffect(() => {
    if (!viewerOpen) return;
    if (isMobile) {
      setOpenMobile(false);
      return;
    }
    if (state !== 'collapsed') {
      setOpen(false);
    }
  }, [viewerOpen, isMobile, setOpen, setOpenMobile, state]);

  return null;
}

interface AppLayoutProps {
  children: ReactNode;
}

export function getRightSlotPresentation({
  visiblePanelMode,
  showRightPanel,
  isWorkflowDrawer,
  canShowMediumRightPanel,
  canShowLargeRightPanel,
}: {
  visiblePanelMode: VisibleRightPanel['mode'];
  showRightPanel: boolean;
  isWorkflowDrawer: boolean;
  canShowMediumRightPanel: boolean;
  canShowLargeRightPanel: boolean;
}): {
  showSplitRightPanel: boolean;
  showCompactGlobalDock: boolean;
  showCompactArtifactDrawer: boolean;
} {
  const canShowSplitRightPanel = isWorkflowDrawer
    ? canShowMediumRightPanel
    : canShowLargeRightPanel;
  const showSplitRightPanel = showRightPanel && canShowSplitRightPanel;

  return {
    showSplitRightPanel,
    showCompactGlobalDock:
      showRightPanel && visiblePanelMode === 'global-dock' && !showSplitRightPanel,
    showCompactArtifactDrawer:
      showRightPanel && visiblePanelMode === 'artifact-drawer' && !showSplitRightPanel,
  };
}

/**
 * Every split companion surface gets a visible grab cue. The project rail is
 * intentionally fixed-width, so showing a drag handle there would promise a
 * resize interaction that cannot happen.
 */
export function shouldShowRightSlotHandle({
  visiblePanelMode,
  showSplitRightPanel,
}: {
  visiblePanelMode: VisibleRightPanel['mode'];
  showSplitRightPanel: boolean;
}): boolean {
  return showSplitRightPanel && visiblePanelMode !== 'project-hub';
}

const AppLayoutComponent = React.memo(({ children }: AppLayoutProps): JSX.Element => {
  const location = useLocation();
  const inspectorOpen = useInspector((s) => s.open);
  const inspectorPresentation = useInspector((s) => s.presentation);
  const videoDialogOpen = useVideoDialogStore((s) => s.isOpen);
  const workflowLibraryOpen = useWorkflowLibraryStore((s) => s.isOpen);
  const [loadedOverlays, setLoadedOverlays] = React.useState({
    inspector: false,
    video: false,
    workflowLibrary: false,
  });

  usePrefetchRoute();
  useWebMCP();
  useGlobalJobMonitor();
  useRememberOpenedFrom();
  useWorkspaceBroadcastSync();
  // Makes `?w=<brandKitId>` addressable app-wide. Mounted here rather than per
  // route so a deep link into ANY surface lands in the workspace it names.
  // Called EXACTLY once — it owns the activation effect, so a second caller
  // would race it. Its verdict is handed to WorkspaceLinkFallback below.
  const { isUnresolvable: workspaceLinkUnresolvable } = useWorkspaceUrlSync();

  const isCreatePage = location.pathname === '/';

  const shouldShowSidebar =
    !location.pathname.startsWith('/onboarding') &&
    !location.pathname.startsWith('/auth') &&
    !location.pathname.startsWith('/reset-password');

  useEffect(() => {
    if (!inspectorOpen && !videoDialogOpen && !workflowLibraryOpen) return;
    setLoadedOverlays((prev) => ({
      inspector: prev.inspector || inspectorOpen,
      video: prev.video || videoDialogOpen,
      workflowLibrary: prev.workflowLibrary || workflowLibraryOpen,
    }));
  }, [inspectorOpen, videoDialogOpen, workflowLibraryOpen]);

  return (
    <>
      <NetworkStatusDialog />
      <WorkspaceLinkFallback isUnresolvable={workspaceLinkUnresolvable} />

      {shouldShowSidebar ? (
        <SidebarProvider
          defaultOpen={false}
          className="h-dvh overflow-hidden bg-background"
          style={{ '--sidebar-width': '14.5rem' } as React.CSSProperties}
        >
          <SettingsDialogProvider>
            <AppSidebar />
            <SidebarAutoCollapse />
            <MainWithOptionalCanvas isCreatePage={isCreatePage}>{children}</MainWithOptionalCanvas>
            <Suspense fallback={null}>
              <SettingsDialog />
            </Suspense>
            <Suspense fallback={null}>
              <GlobalSearchDialog />
            </Suspense>
          </SettingsDialogProvider>
        </SidebarProvider>
      ) : (
        <div className="flex flex-1 flex-col w-full min-h-screen">
          <UnifiedErrorBoundary level="page">{children}</UnifiedErrorBoundary>
        </div>
      )}

      <Suspense fallback={null}>
        {loadedOverlays.video && !(inspectorOpen && inspectorPresentation === 'workspace') ? (
          <VideoStudioDialogMount />
        ) : null}
        {loadedOverlays.inspector ? (
          <GlobalInspectorMount presentationFilter={['modal', 'drawer', 'fullscreen']} />
        ) : null}
        {loadedOverlays.workflowLibrary ? <WorkflowLibraryDialogMount /> : null}
        <HandoffFailureRecovery />
        <LinkedInDestinationPrompt />
      </Suspense>
    </>
  );
});

AppLayoutComponent.displayName = 'AppLayout';

function MainWithOptionalCanvas({
  children,
  isCreatePage,
}: {
  children: ReactNode;
  isCreatePage: boolean;
}) {
  const visiblePanel = useVisibleRightPanel();
  const inspectorOpen = useInspector((s) => s.open);
  const inspectorPresentation = useInspector((s) => s.presentation);
  const closeRightDockPanel = useRightDockStore((s) => s.closePanel);
  const isAgentPanel = useRightDockStore((s) => s.activePanel === 'agent');
  /* Inset right panels use a seamless resize seam: the draggable handle remains,
     but the full-height divider does not compete with the card edge. Keep each
     panel boolean explicit because `isAgentPanel` also controls the agent's
     wider sizeProps below. */
  const isIdeaPanel = useRightDockStore((s) => s.activePanel === 'idea');
  /* 🔴 The ad panel repeated the idea panel's bug, exactly as described above.
     It joined the same inset floating-card presentation in `GlobalRightDock`
     (`'agent' || 'idea' || 'ad'`) and was missed here, so it painted the card's
     own left border AND the full-height resize seam beside it. Founder,
     2026-08-17: _"there should be the draggable handle on that panel, but there
     shouldn't be the divider line — that draggable handle should be there, but
     the full top-to-bottom line shouldn't be."_ That is precisely what
     `seamless` does: it drops the 1px line and keeps the grab handle, which
     `withHandle` supplies independently. Adding a panel to the dock's inset
     presentation without adding it here will reproduce this a fourth time. */
  const isAdPanel = useRightDockStore((s) => s.activePanel === 'ad');
  // Engagement shares the inset dock card, and needs the same handle-without-
  // divider treatment so its left edge does not read as a second panel seam.
  const isEngagementPanel = useRightDockStore((s) => s.activePanel === 'engagement');
  /* The fourth panel the comment above predicted. Sources joins the same inset
     floating-card presentation in `GlobalRightDock`, so it needs the same
     handle-without-divider treatment or it paints the card's own left border
     AND the full-height resize seam beside it. */
  const isSourcesPanel = useRightDockStore((s) => s.activePanel === 'sources');
  /* The FIFTH. Reach's person/company/outreach dock renders through the same
     `GlobalRightDock` → `FloatingPanel` inset card, and shipped without this
     entry, so `/reach/outreach` painted a full-height 900px `bg-border/40`
     seam directly against the card's own `border-border/60` — a double
     boundary the founder read as "the design here looks god awful… so many
     things look very lazy" (2026-08-25).

     ⚠️ This list is the bug. Five panels have now each been added by hand
     AFTER shipping the same visual defect, and the comment above this one
     already predicted the fourth. Every one of these is `activePanel === X`,
     i.e. every one is a global-dock panel, so the condition wants to be
     `visiblePanel.mode === 'global-dock'` and be done forever. That is a
     one-line generalisation, but it silently restyles every OTHER dock panel
     not currently enumerated, which is not this lane's call to make — so this
     adds the missing entry and records the smell instead of quietly widening
     the blast radius. Next person to hit a sixth: make the general fix. */
  const isReachPanel = useRightDockStore((s) => s.activePanel === 'reach');

  // The compact (mobile) global-dock and artifact-drawer surfaces render as
  // true Radix Sheets portaled to document.body with z-50 — outside
  // SidebarInset, so the workspace Inspector's `absolute inset-0` (z-40,
  // scoped to SidebarInset) can never out-stack them the way it does the
  // split-pane desktop variant. Opening a post from the mobile Drafts queue
  // left the queue's Sheet floating on top of the post editor it had just
  // opened underneath — the editor was mounted and functional, just
  // invisible and unreachable (live review). Closing every other right-slot
  // surface the moment a workspace Inspector opens keeps this a one-owner-
  // at-a-time stack, same as the existing project-hub/global-dock/
  // artifact-drawer mutex.
  useEffect(() => {
    if (inspectorOpen && inspectorPresentation === 'workspace') {
      closeOtherRightSlotSurfaces('workspace-inspector');
    }
  }, [inspectorOpen, inspectorPresentation]);

  // The hub + drawer modes are scoped to /create. Global dock is route-agnostic.
  const showRightPanel =
    visiblePanel.mode !== 'empty' && (visiblePanel.mode === 'global-dock' || isCreatePage);

  // The workflow tracker (steps + progress + approve/reject) is core
  // functionality, not a "nice to have" like the project hub — a user running
  // a workflow needs it visible on more window sizes than the default `lg`
  // (1024px) gate allows. Scoped ONLY to the artifact-drawer-in-workflow-mode
  // case so project-hub / global-dock keep their existing `lg` behavior.
  const artifactBaseMode = useArtifactStore((s) => s.baseMode);
  const isWorkflowDrawer =
    visiblePanel.mode === 'artifact-drawer' && artifactBaseMode === 'workflow';
  const canShowMediumRightPanel = useCanSplitRightSlotMedium();
  const canShowLargeRightPanel = useCanSplitRightSlot();

  // Size policy per panel mode:
  // - project-hub:     fixed 28% (no resize handle, ~380px on a 1440px viewport)
  // - global-dock:     resizable 24% default, 24-50%
  // - agent chat:      30%, because it hosts a conversation, not a list
  // - artifact-drawer: resizable 30% default, 20-50%
  //
  // The agent panel rides `global-dock` but cannot share its width. It keeps
  // the wider 30% conversation treatment; utility panels get a slightly wider
  // 24% surface so list content does not feel compressed beside the canvas.
  const sizeProps =
    visiblePanel.mode === 'project-hub'
      ? { defaultSize: 28, minSize: 28, maxSize: 28 }
      : visiblePanel.mode === 'global-dock'
        ? isAgentPanel
          ? { defaultSize: 30, minSize: 24, maxSize: 50 }
          : { defaultSize: 24, minSize: 24, maxSize: 50 }
        : { defaultSize: 30, minSize: 20, maxSize: 50 };

  // Most right-panel modes use the shared 1px resize seam. The agent and idea
  // panels are intentionally seamless: their quiet canvas gap and elevated
  // rounded surface communicate the boundary, matching the compact
  // Claude-style companion panel without sacrificing resize behavior. Project
  // Hub is fixed, so its handle remains a non-interactive visual divider.
  const { showSplitRightPanel, showCompactGlobalDock, showCompactArtifactDrawer } =
    getRightSlotPresentation({
      visiblePanelMode: visiblePanel.mode,
      showRightPanel,
      isWorkflowDrawer,
      canShowMediumRightPanel,
      canShowLargeRightPanel,
    });

  // The compact dock is a Sheet: unmounting it the moment its panel closes
  // leaves Radix no frame to play the exit, so it vanished instead of sliding
  // out. Once it has opened, keep it mounted while the layout stays compact;
  // with no panel it renders a closed Sheet, which paints nothing.
  const [compactDockOpenedOnce, setCompactDockOpenedOnce] = React.useState(false);
  if (showCompactGlobalDock && !compactDockOpenedOnce) setCompactDockOpenedOnce(true);
  const mountCompactGlobalDock =
    showCompactGlobalDock || (compactDockOpenedOnce && !canShowLargeRightPanel);

  const showHandle = showSplitRightPanel;
  const handleIsStatic = visiblePanel.mode === 'project-hub';
  const handleIsHinted = shouldShowRightSlotHandle({
    visiblePanelMode: visiblePanel.mode,
    showSplitRightPanel,
  });

  const mainPanelRef = usePanelRef();
  const rightPanelRef = usePanelRef();
  const initialExpandDone = useRef(false);
  const handleRightPanelResize = React.useCallback(
    (
      panelSize: { asPercentage: number; inPixels: number },
      _id: string | number | undefined,
      prevPanelSize: { asPercentage: number; inPixels: number } | undefined,
    ) => {
      if (visiblePanel.mode !== 'global-dock' || !prevPanelSize) return;
      if (prevPanelSize.asPercentage > 0 && panelSize.asPercentage === 0) {
        closeRightDockPanel();
      }
    },
    [closeRightDockPanel, visiblePanel.mode],
  );

  useLayoutEffect(() => {
    if (!showSplitRightPanel) {
      mainPanelRef.current?.resize(100);
    }
    initialExpandDone.current = true;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isCreatePage) return;
    closeCreateScopedRightSlotSurfaces();
  }, [isCreatePage]);

  useEffect(() => {
    if (!initialExpandDone.current) return;
    if (!showSplitRightPanel) {
      mainPanelRef.current?.resize(100);
    }
  }, [showSplitRightPanel, mainPanelRef]);

  return (
    <RepurposeWorkbenchProvider>
      <Suspense fallback={null}>
        <ProjectHubHydrator />
      </Suspense>
      <SidebarInset className="relative overflow-hidden min-h-0 h-full">
        <ResizablePanelGroup direction="horizontal" className="flex-1 min-h-0 h-full">
          <ResizablePanel
            panelRef={mainPanelRef}
            defaultSize={showSplitRightPanel ? String(100 - sizeProps.defaultSize) : '100'}
            minSize="30"
            className="flex flex-col min-w-0"
          >
            <Navbar />
            <div className="sticky top-0 z-20 bg-background">
              <PageHeaderManager />
            </div>
            <div
              id={isCreatePage ? 'chatRegion' : undefined}
              className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden flex flex-col"
            >
              <UnifiedErrorBoundary level="page">{children}</UnifiedErrorBoundary>
            </div>
          </ResizablePanel>

          {showSplitRightPanel ? (
            <>
              <ResizableHandle
                disabled={handleIsStatic}
                seamless={
                  isAgentPanel ||
                  isIdeaPanel ||
                  isAdPanel ||
                  isEngagementPanel ||
                  isSourcesPanel ||
                  isReachPanel ||
                  visiblePanel.mode === 'artifact-drawer'
                }
                withHandle={handleIsHinted}
                className={cn(
                  showHandle && 'flex',
                  // `disabled` already neutralizes resize; just override the
                  // library's not-allowed cursor for the static seam.
                  handleIsStatic && 'cursor-default',
                )}
              />

              <ResizablePanel
                panelRef={rightPanelRef}
                defaultSize={String(sizeProps.defaultSize)}
                minSize={String(sizeProps.minSize)}
                maxSize={String(sizeProps.maxSize)}
                collapsible
                collapsedSize={0}
                onResize={handleRightPanelResize}
                // Marker for the open animation, which lives in index.css. It cannot
                // live in the className below: react-resizable-panels puts className on
                // an inner wrapper whose flex-grow is always 1, and sizes the outer
                // [data-panel] div instead — which is where data-* lands. The previous
                // `transition-[flex] duration-500` sat on that inner wrapper and so had
                // never once run.
                data-right-slot-pane=""
                className="relative flex-col p-2"
              >
                <UnifiedErrorBoundary level="section">
                  <Suspense fallback={null}>
                    {visiblePanel.mode === 'project-hub' && <ProjectHubMount />}
                    {visiblePanel.mode === 'global-dock' && <GlobalRightDock />}
                    {visiblePanel.mode === 'artifact-drawer' && <ArtifactDrawer />}
                  </Suspense>
                </UnifiedErrorBoundary>
              </ResizablePanel>
            </>
          ) : null}
        </ResizablePanelGroup>
        {(mountCompactGlobalDock || showCompactArtifactDrawer) && (
          <UnifiedErrorBoundary level="section">
            <Suspense fallback={null}>
              {mountCompactGlobalDock && <GlobalRightDock />}
              {showCompactArtifactDrawer && <ArtifactDrawer presentation="sheet" />}
            </Suspense>
          </UnifiedErrorBoundary>
        )}
        {inspectorOpen && inspectorPresentation === 'workspace' ? (
          <UnifiedErrorBoundary level="section">
            <Suspense fallback={null}>
              <GlobalInspectorMount presentationFilter="workspace" />
            </Suspense>
          </UnifiedErrorBoundary>
        ) : null}
      </SidebarInset>
    </RepurposeWorkbenchProvider>
  );
}

const AppLayout = AppLayoutComponent;

export default AppLayout;
